import { getConfig } from '../configs/config';
import {
  AnalyticsConst,
  AnalyticsEventTypes,
  AnalyticsPageView,
  AnalyticsSelectItemEcommerce,
  AnalyticsStandardEvent,
  AnalyticsViewItemEcommerce,
  AnalyticsViewItemListEcommerce,
  ApiError,
  GoogleAnalyticsLimit,
  Purchase,
} from '../types';
import {
  AlertErrorPayload,
  BusinessErrorPayload,
  GeneralErrorPayload,
  TechnicalErrorPayload,
  ValidationErrorPayload,
} from '../types/errors';
import { dataPointsChecker } from '../utils/analyticsUtils';
import generateErrorString from '../utils/errors';
import { checkDataPointsForPII } from '../utils/helpers/piiHelpers';
import {
  getPageReferrer,
  omitBy,
  truncate,
} from '../utils/helpers/utilHelpers';
import resolvePagePath from '../utils/pagePath';

const { NOT_SET } = AnalyticsConst;

/**
 * pageView - a helper for a page view event object
 * @param {Object} context
 * @param {String} context.virtualPagePath Page id name
 * @param {String} context.virtualPagePath Page title (optional)
 */
export const pageView = (context: AnalyticsPageView) => {
  if (!context.virtualPagePath)
    throw new Error(
      `Site Tracking invalid: ${generateErrorString('VirtualPagePath')}`
    );

  context.virtualPagePath = resolvePagePath(context.virtualPagePath);
  context.event = AnalyticsEventTypes.PageView;
  context.virtualPageTitle = context.virtualPageTitle || document.title;
  context.pageReferrer = context.pageReferrer || getPageReferrer();

  // Check Dimension and metrics
  if (getConfig().dataLayerKey && context.data) {
    dataPointsChecker(context.data, getConfig().dataLayerKey);
    context.data = checkDataPointsForPII(context.data);
  }

  return context;
};

/**
 * GA 3 Custom Events
 * @param {*} context
 */
export const customEvent = (context: AnalyticsStandardEvent) => {
  // Set event as submit.
  // if (!eventCategory) context.eventCategory = NOT_SET;
  // Check Dimension and metrics
  if (getConfig().dataLayerKey && context.data) {
    dataPointsChecker(context.data, getConfig().dataLayerKey);
    context.data = checkDataPointsForPII(context.data);
  }

  return context;
};

/**
 * Business Errors Events
 * @param {*} context
 */
export const businessError = ({
  message = '',
  questionId = '',
  errorCode = '',
  errorType = '',
  data = {},
  ...restBusinessErrorPayload
}: BusinessErrorPayload) => {
  // Check Dimension and metrics
  if (getConfig().dataLayerKey && data)
    dataPointsChecker(data, getConfig().dataLayerKey);

  const businessError = omitBy({
    event: AnalyticsEventTypes.BusinessError,
    eventAction: questionId,
    eventLabel: message,
  });

  const dataObject = checkDataPointsForPII(
    omitBy({
      errorCode,
      errorType,
      message,
      ...data,
      ...restBusinessErrorPayload,
    })
  );

  return {
    ...businessError,
    data: {
      ...dataObject,
    },
  };
};

/**
 * Technical Errors Events
 * @param {*} context
 */
export const technicalError = ({
  message = '',
  // questionId = '',
  errorCode = '',
  errorType = '',
  data = {},
  ...restTechnicalErrorPayload
}: TechnicalErrorPayload) => {
  // Check Dimension and metrics
  if (getConfig().dataLayerKey && data)
    dataPointsChecker(data, getConfig().dataLayerKey);

  const technicalError = omitBy({
    event: AnalyticsEventTypes.TechnicalError,
    eventAction: errorType,
    eventLabel: message,
  });

  const dataObject = checkDataPointsForPII(
    omitBy({
      errorCode,
      errorType,
      message,
      ...data,
      ...restTechnicalErrorPayload,
    })
  );

  return {
    ...technicalError,
    data: {
      ...dataObject,
    },
  };
};

/**
 * General Errors Events
 * @param {*} context
 */
export const generalError = ({
  message = '',
  errorCode = '',
  errorType = '',
  questionId = '',
  data = {},
}: GeneralErrorPayload) => {
  if (!message)
    throw new Error(
      `Site Tracking invalid: ${generateErrorString('message', 'questionId')}`
    );

  // Check Dimension and metrics
  if (getConfig().dataLayerKey && data)
    dataPointsChecker(data, getConfig().dataLayerKey);

  const generalError = {
    event: AnalyticsEventTypes.GeneralError,
    eventAction: questionId || errorType || NOT_SET,
    eventLabel: message,
  };

  const dataObject = checkDataPointsForPII(
    omitBy({
      errorCode: errorCode || NOT_SET,
      errorType: errorType || NOT_SET,
      message: message || NOT_SET,
      ...data,
    })
  );
  return {
    ...generalError,
    data: {
      ...dataObject,
    },
  };
};

/**
 * Alert Errors Events
 * @param {*} context
 */
export const alertError = ({ message = '', data = {} }: AlertErrorPayload) => {
  if (!message)
    throw new Error(`Site Tracking invalid: ${generateErrorString('message')}`);

  // Check Dimension and metrics
  if (getConfig().dataLayerKey && data)
    dataPointsChecker(data, getConfig().dataLayerKey);

  const alertError = {
    event: AnalyticsEventTypes.AlertError,
    eventAction: 'display alert error',
    eventLabel: message,
  };

  const dataObject = checkDataPointsForPII(
    omitBy({
      message: message || NOT_SET,
      ...data,
    })
  );
  return {
    ...alertError,
    data: {
      ...dataObject,
    },
  };
};

/**
 * Validation Errors Events
 * @param {*} context
 */
export const validationError = ({
  message = '',
  errorCode = '',
  errorType = '',
  questionId = '',
  data = {},
}: ValidationErrorPayload) => {
  if (!message)
    throw new Error(
      `Site Tracking invalid: ${generateErrorString('message', 'questionId')}`
    );

  // Check Dimension and metrics
  if (getConfig().dataLayerKey && data)
    dataPointsChecker(data, getConfig().dataLayerKey);

  const validationError = {
    event: AnalyticsEventTypes.ValidationError,
    eventAction: questionId || errorType || NOT_SET,
    eventLabel: message,
  };

  const dataObject = checkDataPointsForPII(
    omitBy({
      errorCode: errorCode || NOT_SET,
      errorType: errorType || NOT_SET,
      message: message || NOT_SET,
      ...data,
    })
  );
  return {
    ...validationError,
    data: {
      ...dataObject,
    },
  };
};
/**
 * API Errors Events
 * @param {*} context
 */
export const apiError = (payload: ApiError) => payload;
export const clickTracking = (context: AnalyticsStandardEvent) => {
  const { eventAction = '', data = {} } = context;

  if (!eventAction) {
    throw new Error(
      `Site Tracking invalid: it is required to have ${generateErrorString(
        'eventAction'
      )}`
    );
  }
  // Check Dimension and metrics
  if (getConfig().dataLayerKey && data) {
    dataPointsChecker(data, getConfig().dataLayerKey);
    context.data = checkDataPointsForPII(context.data);
  }

  return context;
};

/**
 * TODO: DEMO purpose recommended to refactor.
 * Click Tracking Event
 * @param {*} context
 */

export const engagementTracking = ({
  eventAction = '',
  eventLabel = '',
  data = {},
  ...rest
}: AnalyticsStandardEvent) => {
  if (!eventAction) {
    throw new Error(
      `Site Tracking invalid: it is required to have ${generateErrorString(
        'eventAction'
      )}`
    );
  }

  // Check Dimension and metrics
  if (getConfig().dataLayerKey && data)
    dataPointsChecker(data, getConfig().dataLayerKey);

  const truncatedEventAction = truncate(
    eventAction,
    GoogleAnalyticsLimit.MAX_EVENT_PARAMETER_LENGTH
  );
  const truncatedEventLabel = truncate(
    eventLabel,
    GoogleAnalyticsLimit.MAX_EVENT_PARAMETER_LENGTH
  );

  return {
    eventAction: truncatedEventAction,
    eventLabel: truncatedEventLabel,
    ...rest, // Spread the remaining properties
    data: {
      ...data, // Spread the remaining data properties
    },
    trackProps: rest.trackProps, // Keep the original trackProps
  };
};

/**
 * Purchase Goal/ Ecommerce Event
 * @param {*} context
 */
export const eCommercePurchase = ({
  data = {
    quoteNumber: '',
    policyId: '',
  },
  ...remainingData
}: Purchase) => {
  const { policyId = '', policyNumber = '' } = data;
  const {
    eventAction = 'payment complete',
    // eventLabel,
    ...restRemainingData
  } = remainingData;

  // Check Dimension and metrics
  if (getConfig().dataLayerKey && data)
    dataPointsChecker(data, getConfig().dataLayerKey);

  const purchaseEvent = {
    event: AnalyticsEventTypes.Purchase,
    eventAction,
    eventLabel: policyId || policyNumber || AnalyticsConst.NOT_SET,
  };

  return {
    ...purchaseEvent,
    data: {
      ...checkDataPointsForPII(data),
    },
    ...restRemainingData,
  };
};

/**
 * Creates an ecommerce event object with the specified event name and parameters.
 *
 * @template T - The type of the input parameter object.
 * @param eventName - The name of the event to be created.
 * @param params - An object containing event details and ecommerce data.
 * @param params.ecommerce - The ecommerce data associated with the event.
 * @param [params.eventAction] - The action associated with the event (optional).
 * @param [params.eventCategory] - The category of the event (optional).
 * @param [params.eventLabel] - The label for the event (optional).
 * @param [params.event] - The event identifier (optional).
 * @param [params.data] - Additional data to be included with the event (optional).
 * @returns An object representing the ecommerce event, including sanitized data.
 */
const createEcommerceEvent = <T>(
  eventName: string,
  {
    ecommerce,
    eventAction = '',
    eventCategory = '',
    eventLabel = '',
    data = {},
  }: T & {
    ecommerce: any;
    eventAction?: string;
    eventCategory?: string;
    eventLabel?: string;
    data?: object;
  }
) => {
  if (getConfig().dataLayerKey && data)
    dataPointsChecker(data, getConfig().dataLayerKey);

  const eventObj = {
    event: eventName,
    eventAction: eventAction || NOT_SET,
    eventCategory: eventCategory || NOT_SET,
    eventLabel: eventLabel || NOT_SET,
    ecommerce,
  };

  return {
    ...eventObj,
    data: {
      ...checkDataPointsForPII(data),
    },
  };
};

/**
 * Creates an ecommerce event for viewing a list of items.
 * @param params - The parameters for the view item list event.
 * @returns The formatted ecommerce event object.
 */
export const eCommerceViewItemList = (params: AnalyticsViewItemListEcommerce) =>
  createEcommerceEvent('view_item_list', params);

/**
 * Creates an ecommerce event for viewing a single item.
 * @param params - The parameters for the view item event.
 * @returns The formatted ecommerce event object.
 */
export const eCommerceViewItem = (params: AnalyticsViewItemEcommerce) =>
  createEcommerceEvent('view_item', params);

/**
 * Creates an ecommerce event for selecting an item from a list.
 * @param params - The parameters for the select item event.
 * @returns The formatted ecommerce event object.
 */
export const eCommerceSelectItem = (params: AnalyticsSelectItemEcommerce) =>
  createEcommerceEvent('select_item', params);
